import {PropsWithChildren, ReactNode, useMemo} from "react";
import jsonLogic from 'json-logic-js';
import {Entity} from "@ots-frontend/ots-data";
import {useAuth} from "./AuthProvider";

export interface ABACContextSubject {
  permissions: string[];
  roles: string[]
  userCompanyType: string
  userCompanyId: string
  userType: string;
  userId: string;
}

export interface ABACContext {
  subject: ABACContextSubject;
  object?: Record<string, never>;
}

export type HasAccessProps = {
  object?: Record<string, any>;
  permissions?: string[]
  empty?: ReactNode
  accessOnEmpty?: boolean
}

export const HasAccess = (props: PropsWithChildren<HasAccessProps>) => {
  const {object, permissions, empty, accessOnEmpty = false, children} = props

  const {hasAccess} = useAuth()

  const _hasAccess = useMemo(() => {
    if (accessOnEmpty && (!permissions || permissions.length === 0)) {
      return true
    }
    return hasAccess(object, ...(permissions ?? []))
  }, [hasAccess, object, permissions, accessOnEmpty])

  return (
    <>
      {_hasAccess ? children : empty}
    </>
  )
}

export type HasEntityAccessProps = Omit<HasAccessProps, 'object' | 'permissions'> & {
  entityDefs: { entity?: Entity, permissions?: string[] }[]
}

export const HasEntityAccess = (props: PropsWithChildren<HasEntityAccessProps>) => {
  const {entityDefs, empty, accessOnEmpty = false, children} = props

  const {hasAccess, hasEntityAccess} = useAuth()

  const _hasAccess = useMemo(() => {
    for (const def of entityDefs) {
      const permissions = def.permissions
      if (accessOnEmpty && (!permissions || permissions.length === 0)) {
        continue
      }

      if (def.entity) {
        if (!hasEntityAccess(def.entity, ...(permissions ?? []))) {
          return false
        }
      } else {
        if (!hasAccess(...(permissions ?? []))) {
          return false
        }
      }
    }

    return true
  }, [hasAccess, hasEntityAccess, entityDefs, accessOnEmpty])

  return (
    <>
      {_hasAccess ? children : empty}
    </>
  )
}

export const hasAccessHelper = (authPermissions: string[], policies: Record<string, any[]>, contextOrPerm: ABACContext | string, ...rest: string[]): boolean => {
  let context: ABACContext | undefined;
  let perms: string[]
  let roles: string[]

  if (contextOrPerm && typeof contextOrPerm === 'object' && 'subject' in contextOrPerm) {
    context = contextOrPerm as ABACContext;
    perms = rest;
    roles = (contextOrPerm as ABACContext).subject?.roles || [];
  } else {
    context = undefined;
    perms = contextOrPerm ? ([contextOrPerm as string] as string[]).concat(rest) : rest;
    roles = []
  }

  if (!perms || perms.length === 0) {
    return true;
  }

  if (!roles.includes("SUPER_ADMIN")) {
    for (const perm of perms) {
      if (!authPermissions.includes(perm)) {
        return false
      }

      const rules = policies[perm];
      if (rules && context) {
        for (const rule of rules) {
          if (!jsonLogic.apply(rule, context)) {
            return false
          }
        }
      }
    }
  }

  return true
}
